import type {
  ApplyApiKeyModelNamespaceResponse,
  ListApiKeyModelMappingsResponse,
  SetApiKeyAutoModelNamespaceResponse,
  UpsertApiKeyModelMappingResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import {
  applyKeyModelNamespace,
  deleteKeyModelMapping,
  disableKeyAutoModelNamespace,
  enableKeyAutoModelNamespace,
  listKeyModelMappings,
  upsertKeyModelMapping,
} from './keyModelMappings.ts';

/**
 * React Query wrappers around `/v1/keys/:id/model-mappings` (epic #898
 * P6 #913).
 *
 * Query key conventions:
 *   - `['keys', keyId, 'model-mappings']` — per-key mapping list
 *
 * Both mutations invalidate only the affected key's list; the `/v1/keys`
 * summary rows carry no mapping data, so the `['keys']` list query does
 * not need to refetch.
 */

export function keyModelMappingsQueryKey(keyId: string): readonly [string, string, string] {
  return ['keys', keyId, 'model-mappings'] as const;
}

export function useKeyModelMappings(
  keyId: string | undefined,
): UseQueryResult<ListApiKeyModelMappingsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey:
      keyId === undefined
        ? (['keys', 'missing', 'model-mappings'] as const)
        : keyModelMappingsQueryKey(keyId),
    queryFn: ({ signal }) => {
      if (keyId === undefined) return Promise.reject(new Error('missing key id'));
      return listKeyModelMappings(keyId, fetchImpl, signal);
    },
    enabled: keyId !== undefined,
  });
}

export interface UpsertKeyModelMappingVars {
  readonly keyId: string;
  readonly alias: string;
  readonly skuId: string;
}

export function useUpsertKeyModelMapping(): UseMutationResult<
  UpsertApiKeyModelMappingResponse,
  Error,
  UpsertKeyModelMappingVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ keyId, alias, skuId }) =>
      upsertKeyModelMapping(keyId, alias, { sku_id: skuId }, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: keyModelMappingsQueryKey(vars.keyId) });
    },
  });
}

export interface DeleteKeyModelMappingVars {
  readonly keyId: string;
  readonly alias: string;
}

export function useDeleteKeyModelMapping(): UseMutationResult<
  null,
  Error,
  DeleteKeyModelMappingVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ keyId, alias }) => deleteKeyModelMapping(keyId, alias, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: keyModelMappingsQueryKey(vars.keyId) });
    },
  });
}

export interface KeyModelNamespaceVars {
  readonly keyId: string;
  readonly namespaceId: string;
}

export function useApplyKeyModelNamespace(): UseMutationResult<
  ApplyApiKeyModelNamespaceResponse,
  Error,
  KeyModelNamespaceVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ keyId, namespaceId }) => applyKeyModelNamespace(keyId, namespaceId, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: keyModelMappingsQueryKey(vars.keyId) });
    },
  });
}

export function useEnableKeyAutoModelNamespace(): UseMutationResult<
  SetApiKeyAutoModelNamespaceResponse,
  Error,
  KeyModelNamespaceVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ keyId, namespaceId }) =>
      enableKeyAutoModelNamespace(keyId, namespaceId, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: keyModelMappingsQueryKey(vars.keyId) });
    },
  });
}

export function useDisableKeyAutoModelNamespace(): UseMutationResult<
  null,
  Error,
  KeyModelNamespaceVars
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ keyId, namespaceId }) =>
      disableKeyAutoModelNamespace(keyId, namespaceId, fetchImpl),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: keyModelMappingsQueryKey(vars.keyId) });
    },
  });
}
